import type {
  ItemContentData,
  ImageMetaDataType,
  VideoMetaDataType,
} from '@wix/fast-gallery-library';
import {
  cssStringToBuilderReturnType,
  MediaTypes,
} from '@wix/fast-gallery-library';
import type {
  IWixSDKItemLink,
  OrganizeMediaPanelItems,
  WixSDKItems,
} from '../FastGallery.types';

export const convertCustomCssToSelectorToCss = (customCss: string) => {
  if (!customCss) {
    return {};
  }
  try {
    const customCssObj = cssStringToBuilderReturnType(
      customCss.replaceAll('\n', ''),
    ).cssObject;

    const splitSelectorsCssObject: any = {};
    for (const [selectors, properties] of Object.entries(customCssObj)) {
      selectors.split(',').forEach(selector => {
        const trimmedSelector = selector.trim();
        splitSelectorsCssObject[trimmedSelector] = properties;
      });
    }
    return splitSelectorsCssObject;
  } catch {
    return {};
  }
};

export const formatWixCodeLinkToObject = (link?: IWixSDKItemLink) => {
  if (link && typeof link === 'object') {
    return {
      url: link?.url || '',
      target: link?.target || '_blank',
    };
  }
  if (typeof link === 'string') {
    return {
      url: link,
      target: '_blank',
    };
  }

  return {
    target: '_blank',
    url: '',
  };
};

export function extractItemSrcData(
  src: string = '',
): { uri: string; width: number; height: number } | null {
  const regex =
    /wix:image:\/\/v1\/([^/]+)\/[^#]+#originWidth=(\d+)&originHeight=(\d+)/;
  const match = src.match(regex);

  if (match) {
    const [, uri, width, height] = match;
    return {
      uri,
      width: parseInt(width, 10),
      height: parseInt(height, 10),
    };
  } else {
    return null;
  }
}

const getGalleryMediaType = (type: string) => {
  return (
    {
      image: MediaTypes.IMAGE,
      video: MediaTypes.VIDEO,
    }[type.toLowerCase()] || MediaTypes.IMAGE
  );
};
export const convertOrganizeMediaPanelItemsToContentItems = (
  items: Array<OrganizeMediaPanelItems>,
): Array<ItemContentData<ImageMetaDataType | VideoMetaDataType>> => {
  return items.map((item, index) => {
    const mediaType = getGalleryMediaType(item?.type);
    return {
      id: item?.id || `item-${index}`,
      index: index.toString(),
      uri: item.uri,
      mediaUrl: 'https://static.wixstatic.com/media/' + item.uri,
      metaData: {
        type: mediaType,
        width: item?.width || 400,
        height: item?.height || 400,
        title: item?.title || '',
        description: item?.description
          ? item?.description
          : 'I’m a paragraph. Click here to add your own text and edit me.',
        alt: item?.alt || '',
        link: {
          url: '',
          target: '_blank',
        },
        fileName: item.uri,
      },
      type: mediaType,
    };
  });
};

export const convertWixSDKItemsToContentItems = (
  items: WixSDKItems,
): Array<ItemContentData<ImageMetaDataType | VideoMetaDataType>> => {
  return items?.map((item, index) => {
    const itemLink = formatWixCodeLinkToObject(item?.link);
    const srcData = extractItemSrcData(item.src);
    const itemAlt = 'alt' in item ? item?.alt : '';
    const mediaType = getGalleryMediaType(item?.type);
    const convertedItem: ItemContentData<
      ImageMetaDataType | VideoMetaDataType
    > = {
      id: `item-${index}`,
      index: index.toString(),
      mediaUrl: 'https://static.wixstatic.com/media/' + srcData?.uri,
      uri: srcData?.uri || '',
      metaData: {
        type: mediaType,
        width: srcData?.width || 400,
        height: srcData?.height || 400,
        title: item?.title || '',
        description:
          item?.description ||
          'I’m a paragraph. Click here to add your own text and edit me.',
        alt: itemAlt || '',
        link: itemLink,
      },
      type: mediaType,
    };
    if (
      convertedItem.metaData.type === MediaTypes.IMAGE &&
      'settings' in item
    ) {
      convertedItem.metaData.focalPoint = {
        x: item.settings?.focalPoint?.at(0) || 0.5,
        y: item.settings?.focalPoint?.at(1) || 0.5,
      };
    }
    return convertedItem;
  });
};
